import { useEffect, useMemo, useState } from 'react';

import { ChevronLeft, ChevronRight, Info, RefreshCw } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useConfigurations } from '@/hooks/useConfigurations';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useTwilioNumbers } from '@/hooks/useTwilioNumbers';
import { twilioCostsApi } from '@/lib/api/twilio-costs.api';
import { formatDateTime } from '@/utils/format';

import type { TwilioCostsSummary } from '@repo/shared-types';

function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function formatCurrency(amount: number): string {
  return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function getDaysInMonth(month: string): number {
  const [year, monthNum] = month.split('-').map(Number);
  return new Date(year, monthNum, 0).getDate();
}

const PAGE_SIZE = 50;

interface NumberUsage {
  inbound: string[];
  outbound: string[];
}

export const TwilioCostsPage = () => {
  usePageTitle('Twilio Costs');
  const [searchParams, setSearchParams] = useSearchParams();
  const month = searchParams.get('month') || getCurrentMonth();
  const dayParam = searchParams.get('day');
  const day = dayParam ? parseInt(dayParam, 10) : undefined;
  const pageParam = searchParams.get('page');
  const page = pageParam ? parseInt(pageParam, 10) : 1;
  const phoneNumberParam = searchParams.get('phoneNumber') || '';

  const { data: numbers = [], isLoading: numbersLoading } = useTwilioNumbers();
  const { data: configurations = [] } = useConfigurations();

  const [data, setData] = useState<TwilioCostsSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const usageByNumberId = useMemo(() => {
    const map = new Map<string, NumberUsage>();
    numbers.forEach((n) => {
      const inbound = n.inbound_configuration_name ? [n.inbound_configuration_name] : [];
      map.set(n.id, { inbound, outbound: [] });
    });
    configurations.forEach((c) => {
      const id = c.default_outbound_twilio_phone_number_id;
      if (!id) return;
      const entry = map.get(id);
      if (entry) {
        entry.outbound.push(c.name);
      }
    });
    return map;
  }, [numbers, configurations]);

  const usageByPhoneNumber = useMemo(() => {
    const map = new Map<string, NumberUsage>();
    numbers.forEach((n) => {
      const usage = usageByNumberId.get(n.id);
      if (usage) map.set(n.phone_number, usage);
    });
    return map;
  }, [numbers, usageByNumberId]);

  const selectedUsage = phoneNumberParam ? usageByPhoneNumber.get(phoneNumberParam) : undefined;

  const fetchData = async (refresh = false) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await twilioCostsApi.getByPhoneNumber(
        month,
        day,
        page,
        PAGE_SIZE,
        refresh,
        phoneNumberParam || undefined,
      );
      setData(result);
    } catch (err) {
      console.error('Failed to fetch Twilio costs:', err);
      setError('Failed to load Twilio costs. Please check your Twilio configuration.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, day, page, phoneNumberParam]);

  const updateParams = (updates: Record<string, string | undefined>) => {
    const params: Record<string, string> = { month };
    if (day) params.day = String(day);
    if (page > 1) params.page = String(page);
    if (phoneNumberParam) params.phoneNumber = phoneNumberParam;
    // Apply updates
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        params[key] = value;
      } else {
        delete params[key];
      }
    });
    setSearchParams(params);
  };

  const handleMonthChange = (newMonth: string) => {
    updateParams({ month: newMonth, page: undefined });
  };

  const handleDayChange = (newDay: string) => {
    updateParams({ day: newDay || undefined, page: undefined });
  };

  const handlePhoneNumberChange = (newPhoneNumber: string) => {
    updateParams({ phoneNumber: newPhoneNumber || undefined, page: undefined });
  };

  const handlePageChange = (newPage: number) => {
    updateParams({ page: newPage > 1 ? String(newPage) : undefined });
  };

  const handleRefresh = () => {
    fetchData(true);
  };

  const daysInMonth = getDaysInMonth(month);

  const numberOptions = useMemo(() => {
    return [...numbers].sort((a, b) => {
      // Active first, then by phone number
      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
      return a.phone_number.localeCompare(b.phone_number);
    });
  }, [numbers]);

  const renderUsageLabel = (usage: NumberUsage | undefined) => {
    if (!usage || (usage.inbound.length === 0 && usage.outbound.length === 0)) {
      return (
        <span className="text-muted-foreground inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium dark:bg-gray-800">
          Unassigned
        </span>
      );
    }
    return (
      <div className="flex flex-wrap gap-1.5">
        {usage.inbound.map((name) => (
          <span
            key={`in-${name}`}
            className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
          >
            Inbound · {name}
          </span>
        ))}
        {usage.outbound.map((name) => (
          <span
            key={`out-${name}`}
            className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
          >
            Outbound · {name}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-10">
      <div className="space-y-3">
        <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Infrastructure</p>
        <h1 className="text-4xl font-bold tracking-tight">Twilio Costs</h1>
        <p className="text-muted-foreground max-w-3xl">
          Twilio usage breakdown per phone number. Data is cached for 1 hour.
        </p>
        <div className="bg-muted/50 flex items-start gap-3 rounded-lg border p-4">
          <Info className="text-muted-foreground mt-0.5 h-5 w-5 shrink-0" />
          <div className="text-muted-foreground text-sm">
            <p className="font-medium">Usage tracking</p>
            <p className="mt-1">
              Costs are calculated from individual call and message records. Prices shown are in USD
              as reported by Twilio.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <label htmlFor="phone-number-picker" className="text-sm font-medium">
            Phone number
          </label>
          <select
            id="phone-number-picker"
            value={phoneNumberParam}
            onChange={(e) => handlePhoneNumberChange(e.target.value)}
            disabled={numbersLoading}
            className="border-input bg-background ring-offset-background focus-visible:ring-ring min-w-[280px] rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
          >
            <option value="">Default (FROM_PHONE_NUMBER)</option>
            {numberOptions.map((n) => {
              const usage = usageByNumberId.get(n.id);
              const labelParts: string[] = [n.phone_number];
              if (n.friendly_name && n.friendly_name !== n.phone_number) {
                labelParts.push(`(${n.friendly_name})`);
              }
              const usedBy = [
                ...(usage?.inbound.map((name) => `in:${name}`) ?? []),
                ...(usage?.outbound.map((name) => `out:${name}`) ?? []),
              ];
              if (usedBy.length > 0) {
                labelParts.push(`— ${usedBy.join(', ')}`);
              } else {
                labelParts.push('— unassigned');
              }
              if (!n.is_active) {
                labelParts.push('[inactive]');
              }
              return (
                <option key={n.id} value={n.phone_number}>
                  {labelParts.join(' ')}
                </option>
              );
            })}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="month-picker" className="text-sm font-medium">
            Month
          </label>
          <input
            id="month-picker"
            type="month"
            value={month}
            onChange={(e) => handleMonthChange(e.target.value)}
            max={getCurrentMonth()}
            className="border-input bg-background ring-offset-background focus-visible:ring-ring rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="day-picker" className="text-sm font-medium">
            Day
          </label>
          <select
            id="day-picker"
            value={day || ''}
            onChange={(e) => handleDayChange(e.target.value)}
            className="border-input bg-background ring-offset-background focus-visible:ring-ring rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
          >
            <option value="">All month</option>
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isLoading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {phoneNumberParam && (
        <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium">Used by:</span>
          {renderUsageLabel(selectedUsage)}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="border-primary mx-auto h-12 w-12 animate-spin rounded-full border-b-2" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      ) : !data ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              No Twilio usage data available for {month}
              {day ? ` day ${day}` : ''}.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Phone Number Header */}
          <Card className="border-primary/20 bg-primary/5">
            <CardHeader className="pb-2">
              <CardDescription>Phone Number</CardDescription>
              <CardTitle className="font-mono text-2xl">{data.phoneNumber}</CardTitle>
              <div className="pt-2">
                {renderUsageLabel(usageByPhoneNumber.get(data.phoneNumber))}
              </div>
            </CardHeader>
          </Card>

          {/* Summary Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            <Card className="border-dashed">
              <CardHeader className="pb-2">
                <CardDescription>Total Cost</CardDescription>
                <CardTitle className="text-3xl">{formatCurrency(data.totalCost)}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  {data.currency} &middot; {month}
                  {day ? ` day ${day}` : ''}
                </p>
              </CardContent>
            </Card>
            <Card className="border-dashed">
              <CardHeader className="pb-2">
                <CardDescription>Calls</CardDescription>
                <CardTitle className="text-3xl">{data.callCount.toLocaleString()}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  {formatCurrency(data.callCost)} total
                </p>
              </CardContent>
            </Card>
            <Card className="border-dashed">
              <CardHeader className="pb-2">
                <CardDescription>Call Minutes</CardDescription>
                <CardTitle className="text-3xl">{data.callMinutes.toFixed(1)}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">Total duration</p>
              </CardContent>
            </Card>
            <Card className="border-dashed">
              <CardHeader className="pb-2">
                <CardDescription>SMS</CardDescription>
                <CardTitle className="text-3xl">{data.smsCount.toLocaleString()}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  {formatCurrency(data.smsCost)} total
                </p>
              </CardContent>
            </Card>
            {data.cachedAt && (
              <Card className="border-dashed">
                <CardHeader className="pb-2">
                  <CardDescription>Last Updated</CardDescription>
                  <CardTitle className="text-lg">{formatDateTime(data.cachedAt)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">Cached for 1 hour</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Calls Breakdown Table */}
          <Card>
            <CardHeader>
              <CardTitle>Call Breakdown</CardTitle>
              <CardDescription>
                Individual calls made from {data.phoneNumber}, sorted by most recent
              </CardDescription>
            </CardHeader>
            <CardContent>
              {data.calls.length === 0 ? (
                <p className="text-muted-foreground py-8 text-center">
                  No calls recorded for this period.
                </p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="pb-3 text-left font-medium">Date/Time</th>
                          <th className="pb-3 text-left font-medium">To</th>
                          <th className="pb-3 text-left font-medium">Direction</th>
                          <th className="pb-3 text-left font-medium">Status</th>
                          <th className="pb-3 text-right font-medium">Duration</th>
                          <th className="pb-3 text-right font-medium">Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.calls.map((call) => (
                          <tr key={call.sid} className="border-b last:border-0">
                            <td className="py-3">{formatDateTime(call.startTime)}</td>
                            <td className="py-3 font-mono">{call.to}</td>
                            <td className="py-3">
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                  call.direction === 'outbound-api'
                                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                    : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                                }`}
                              >
                                {call.direction}
                              </span>
                            </td>
                            <td className="py-3">
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                  call.status === 'completed'
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                    : call.status === 'busy' ||
                                        call.status === 'failed' ||
                                        call.status === 'no-answer'
                                      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                      : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                                }`}
                              >
                                {call.status}
                              </span>
                            </td>
                            <td className="py-3 text-right font-mono">
                              {formatDuration(call.duration)}
                            </td>
                            <td className="py-3 text-right font-mono">
                              {formatCurrency(call.cost)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 font-semibold">
                          <td className="pt-3" colSpan={4}>
                            Total ({data.callCount} calls)
                          </td>
                          <td className="pt-3 text-right font-mono">
                            {data.callMinutes.toFixed(1)} min
                          </td>
                          <td className="pt-3 text-right font-mono">
                            {formatCurrency(data.callCost)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {/* Pagination */}
                  {data.pagination.totalPages > 1 && (
                    <div className="mt-4 flex items-center justify-between border-t pt-4">
                      <p className="text-muted-foreground text-sm">
                        Showing {(data.pagination.page - 1) * data.pagination.pageSize + 1} to{' '}
                        {Math.min(
                          data.pagination.page * data.pagination.pageSize,
                          data.pagination.totalCalls,
                        )}{' '}
                        of {data.pagination.totalCalls} calls
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(data.pagination.page - 1)}
                          disabled={data.pagination.page <= 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Previous
                        </Button>
                        <span className="text-muted-foreground text-sm">
                          Page {data.pagination.page} of {data.pagination.totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(data.pagination.page + 1)}
                          disabled={data.pagination.page >= data.pagination.totalPages}
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
